import type { FeatureCollection, Point } from "geojson"
import type { IGeoJsonProps } from "@features/Pages/TimelineMapPage/_js/app/interfaces/IGeoJsonFeature"
import { getQuerystringNameValue } from "./domUtils"
import { useTimelineMapStore } from "@features/Pages/TimelineMapPage/_js/app/store/timelineMapStore"

type MapController = {
  setData: (
    data: FeatureCollection<Point, IGeoJsonProps>,
    focusId?: string | number | null
  ) => void
  destroy: () => void
}

let mapController: MapController | null = null
let currentContainerId: string | null = null

export async function initMap(
  containerId: string,
  fc: FeatureCollection<Point, IGeoJsonProps>
): Promise<void> {
  
  const container = document.getElementById(containerId)
  if (!container) { return }

  const store = useTimelineMapStore();
  const MAPBOX_TOKEN = store.mapboxConfig.accessToken
  const MAPBOX_STYLE_ID = store.mapboxConfig.styleId

  const isIE = document.documentElement.classList.contains("ie")

  if (isIE) {
    // Static IE-map
    const width = window.innerWidth > 1280 ? 1280 : window.innerWidth || 1280
    const centerLng = 20.7424
    const centerLat = 58.7942
    const zoom = 6.54
    const bearing = 0
 
    if (!MAPBOX_TOKEN || !MAPBOX_STYLE_ID) { return }

    const encoded = encodeURIComponent(JSON.stringify(fc))
    const url =
      `https://api.mapbox.com/styles/v1/smtm/${MAPBOX_STYLE_ID}/static/` +
      `geojson(${encoded})/` +
      `${centerLng},${centerLat},${zoom},${bearing}/` +
      `${width}x600?access_token=${MAPBOX_TOKEN}`

    const img = document.createElement("img")
    img.className = "start-page-static-map"
    img.src = url
    img.alt = "WreckWidgetMap"

    if (mapController) {
      mapController.destroy()
      mapController = null
      currentContainerId = null
    }

    container.innerHTML = ""
    container.appendChild(img)
    return
  }

  // Interactive map
  try {
    if (!MAPBOX_TOKEN || !MAPBOX_STYLE_ID) { return }

    const id: string | null = getQuerystringNameValue("id")
    const { createMapBoxGlController } = await import(
      "@features/Pages/TimelineMapPage/_js/app/services/vrak/initMapBoxGl"
      )

    const options = {
      mapboxAccessToken: MAPBOX_TOKEN,
      mapboxStyle: `mapbox://styles/smtm/${MAPBOX_STYLE_ID}`,
      translations: store.translations
    }

    if (!mapController || currentContainerId !== containerId) {
      if (mapController) {
        mapController.destroy()
        mapController = null
        currentContainerId = null
      }
      container.innerHTML = ""

      mapController = createMapBoxGlController(containerId, fc, id ?? null, options)
      currentContainerId = containerId
      return
    }

    mapController.setData(fc, id ?? null)
  } catch (err) {
    console.error("Could not load initMapBoxGl:", err)
  }
}

export function destroyMap(): void {
  if (!mapController) { return }
  mapController.destroy()
  mapController = null
  currentContainerId = null
}

export default initMap
